<script setup>
import { ref, onMounted, computed, nextTick } from 'vue'
import { useStepper } from '@/composables/stepper'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { stringToColor, textColor } from '@/lib/utils'

import { useNotify } from '@/composables/notify'
import { useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'
import { useArchive } from '@/composables/archive'
import { useClone } from '@/composables/object'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import ServiceSelector from '@/components/shared/ServiceSelector.vue'
import ServiceMultiSelect from '@/components/shared/ServiceMultiSelect.vue'
import GroupNameDialog from '@/components/shared/GroupNameDialog.vue'
import RateCardInfo from '@/components/shared/RateCardInfo.vue'
import FormSectionTitle from '@/components/shared/FormSectionTitle.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  listingId: {
    type: Number,
    required: true,
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { step, nextStep, prevStep, markAttempted } = useStepper()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { notifySuccess, notifyError } = useNotify()

const form = ref(null)
const serviceFilter = ref('')

const groupDialogOpen = ref(false)
const isCreatingGroup = ref(false)

// Set when editing a listing item that points to a virtual group: enables inline editing
// of the group's components/quantities instead of the single service selector.
const editingGroupId = ref(null)

// `serviceComponents` (multi-selection as { id, quantity }) is folded into editItem so
// useIsUpdated tracks it: on create it drives the group build, on edit of a virtual group
// it holds the group's components. Stripped before persisting the listing item.
const editItem = ref(
  props.modelValue.id
    ? useClone(props.modelValue)
    : {
        listing: props.listingId,
        name: '',
        description: '',
        tags: [],
        service: null,
        serviceComponents: [],
        price: null,
        cost: null,
        currency_ref: webSiteStore.getDefaultCurrency()?.id,
        unit_of_measure_ref: webSiteStore.getDefaultUnitOfMeasure()?.id,
        vat_type_ref: webSiteStore.getDefaultVatType()?.id,
      }
)

const { isUpdated, setOriginal } = useIsUpdated(editItem)

const isFreePrice = computed(() => !(Number(editItem.value.price) > 0))
const showZeroPriceDialog = ref(false)

onMounted(async () => {
  const serviceFetches = []
  if (planningStore.equipments.length === 0) {
    serviceFetches.push(planningStore.fetchEquipmentsAsync({ all: true }, true))
  }
  if (planningStore.staff.length === 0) {
    serviceFetches.push(planningStore.fetchStaffsAsync({ all: true }, true))
  }
  if (planningStore.groups.length === 0) {
    serviceFetches.push(planningStore.fetchGroupsAsync({ all: true }, true))
  }
  if (serviceFetches.length > 0) await Promise.all(serviceFetches)

  if (props.modelValue?.id) {
    const res = (await planningStore.fetchListingItemByIdAsync(props.modelValue.id))?.data
    if (res) {
      editItem.value = { ...res }
      // initialize tags array object list
      editItem.value.tags = (editItem.value.tags || [])
        .map(id => availableTags.value.find(t => t.id === id))
        .filter(Boolean)
    }

    const group = planningStore.getGroupById(editItem.value.service)
    if (group?.structure_type === portalStore.serviceStructureTypeEnum.GROUP_VIRTUAL) {
      editingGroupId.value = group.id
      editItem.value.serviceComponents = (group.components || []).map(c => ({ id: c.id, quantity: c.quantity }))
    }

    setOriginal(editItem.value)
  }
})

const availableTags = computed(() => {
  const filter = tagFilter.value
    ? t => t.name?.toLowerCase().includes(tagFilter.value)
    : () => true
  return portalStore.getLocalizedTags().filter(filter)
})

const availableServices = computed(() => {
  let services = planningStore.getServices().filter(s => !s.archived)
  if (serviceFilter.value) {
    const lower = serviceFilter.value.toLowerCase()
    services = services.filter(s => s.name?.toLowerCase().includes(lower))
  }
  // Order by service type (staff → equipment → group, matching serviceTypeEnum), then by name.
  return [...services].sort((a, b) => a.type - b.type || (a.name || '').localeCompare(b.name || ''))
})

const onServiceFilter = (val, update) => {
  update(() => { serviceFilter.value = val.toLowerCase() })
}


const { archiveWarning, archiveAsync, onRowRemoving: onDelete } = useArchive({
  onArchive: async id => {
    await planningStore.archiveListingItemAsync(id)
  },
  onDelete: async (id, config) => {
    await planningStore.deleteListingItemAsync(id, config)
    notifySuccess(i18n.t('info.deletedSuccessfully'))
    emit('delete')
  },
})

const persistListingItemAsync = async () => {
  const updateItem = {
    ...editItem.value,
    tags: (editItem.value.tags || []).map(t => t.id ?? t),
  }
  delete updateItem.serviceComponents
  const saved = await planningStore.upsertListingItemAsync(updateItem)
  updateItem.id = updateItem.id || saved.data.id
  notifySuccess(
    editItem.value.id ? i18n.t('info.updatedSuccessfully') : i18n.t('info.createdSuccessfully')
  )
  emit('update', updateItem)
}

// Submit can be triggered from any step (edit mode's Update button), where the services
// field is unmounted; jump back to it so its standard validation surfaces.
const ensureServiceComponents = async () => {
  if (editItem.value.serviceComponents.length > 0) return true
  step.value = 2
  await nextTick()
  await form.value?.validate()
  return false
}

const onUpdate = async () => {
  if (form.value) {
    const valid = await form.value.validate()
    if (!valid) {
      return
    }
  }

  // Edit mode: a virtual group item lets you extend the group inline; a plain-service
  // item keeps the existing single-service behaviour.
  if (editItem.value.id) {
    if (editingGroupId.value && !(await ensureServiceComponents())) {
      return
    }
    try {
      if (editingGroupId.value) {
        await planningStore.upsertGroupAsync({ id: editingGroupId.value, components: editItem.value.serviceComponents })
        await planningStore.fetchGroupsAsync({ all: true }, true)
      }
      await persistListingItemAsync()
    } catch (error) {
      console.error(error)
      notifyError(i18n.t('error.saveFailed'))
    }
    return
  }

  // Create mode: at least one service is required.
  if (!(await ensureServiceComponents())) {
    return
  }

  if (isFreePrice.value) {
    showZeroPriceDialog.value = true
    return
  }

  await createListingItemAsync()
}

const createListingItemAsync = async () => {
  // Multiple services → ask for a group name and build a virtual group first.
  if (editItem.value.serviceComponents.length > 1) {
    groupDialogOpen.value = true
    return
  }

  // Single service → plain listing item pointing to that service.
  editItem.value.service = editItem.value.serviceComponents[0].id
  try {
    await persistListingItemAsync()
  } catch (error) {
    console.error(error)
    notifyError(i18n.t('error.saveFailed'))
  }
}

const onGroupConfirm = async (groupName) => {
  isCreatingGroup.value = true
  try {
    const payload = {
      name: groupName,
      code: String(await planningStore.fetchNextGroupIdAsync()),
      tags: [],
      components: editItem.value.serviceComponents,
      structure_type: portalStore.serviceStructureTypeEnum.GROUP_VIRTUAL,
      type: portalStore.serviceTypeEnum.EQUIPMENT,
      availability_type: portalStore.serviceAvailabilityTypeEnum.USUALLY_AVAILABLE,
      currency_ref: webSiteStore.getDefaultCurrency()?.id,
      rc_currency_ref: webSiteStore.getDefaultCurrency()?.id,
      rc_unit_of_measure_ref: webSiteStore.getDefaultUnitOfMeasure()?.id,
      rc_vat_type_ref: webSiteStore.getDefaultVatType()?.id,
    }
    const group = (await planningStore.upsertGroupAsync(payload))?.data
    // Refresh groups so getServices() can resolve the new group for display/reuse.
    await planningStore.fetchGroupsAsync({ all: true }, true)
    editItem.value.service = group.id
    await persistListingItemAsync()
    groupDialogOpen.value = false
  } catch (error) {
    console.error(error)
    notifyError(i18n.t('error.saveFailed'))
  } finally {
    isCreatingGroup.value = false
  }
}

const tagFilter = ref('')

const onTagFilter = (val, update) => {
  if (val === '') {
    update(() => {
      tagFilter.value = ''
    })
    return
  }

  update(() => {
    tagFilter.value = val.toLowerCase()
  })
}

const showDeleteDialog = ref(false)
const onConfirmDelete = () => { showDeleteDialog.value = true }

const emit = defineEmits(['update', 'cancel', 'delete'])
useNavigator(() => emit('cancel'))
useEscapeClose(isUpdated, () => emit('cancel'))
const onCancel = () => emit('cancel')
</script>

<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => {
        await archiveAsync(archiveWarning.item.id, false)
        emit('update')
      }"
    />
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDelete({ id: editItem.id })"
    />
    <confirm-dialog
      v-model="showZeroPriceDialog"
      :message="i18n.t('warning.zeroPriceListingItemConfirm')"
      icon="mdi-alert"
      icon-color="warning"
      @confirm="createListingItemAsync"
    />
    <navigate-back
      :text="editItem?.id ? i18n.t('common.editListingItem') : i18n.t('common.addListingItem')"
      @cancel="onCancel"
    />
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll" :class="{ 'dialog-card-content': editItem?.id }">
      <q-card-section class="q-px-sm q-pt-none">
        <q-form ref="form">
          <q-stepper v-model="step" class="q-py-none">
            <q-step
              :name="1"
              :title="i18n.t('common.basicInfo')"
              icon="mdi-folder-plus"
              :done="!!editItem.id || step > 1"
              :header-nav="!!editItem.id || step > 1"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.name"
                  class="col-12"
                  :label="i18n.t('common.name')"
                  autofocus
                  required
                  :rules="[val => val && val.length > 0 || i18n.t('common.required')]"
                />
                <form-section-title :label="i18n.t('common.details')" />
                <q-input
                  v-model="editItem.description"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.description')"
                  autogrow
                />
                <q-select
                  v-model="editItem.tags"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  use-chips
                  multiple
                  use-input
                  :label="i18n.t('common.tag', 2)"
                  option-value="id"
                  option-label="name"
                  :options="availableTags"
                  @filter="onTagFilter"
                >
                  <template #selected-item="chip">
                    <q-chip
                      removable
                      size="sm"
                      :tabindex="chip.tabindex"
                      class="q-ma-none q-mr-xs"
                      :style="{ 'background-color': stringToColor(chip.opt.name), 'color': textColor(stringToColor(chip.opt.name)) }"
                      @remove="chip.removeAtIndex(chip.index)"
                    >
                      {{ chip.opt.name }}
                    </q-chip>
                  </template>
                </q-select>
              </div>

              <stepper-navigation
                :continue="() => { markAttempted(1); form.validate().then(success => (success ? nextStep() : null)) }"
              />
            </q-step>
            <q-step
              :name="2"
              :title="i18n.t('common.selectServices')"
              icon="mdi-filmstrip"
              :done="!!editItem.id || step > 2"
              :header-nav="!!editItem.id || step > 2"
            >
              <!-- A virtual group is built on finish when more than one service is picked -->
              <service-multi-select
                v-if="!editItem.id || editingGroupId"
                v-model="editItem.serviceComponents"
                :label="i18n.t('common.service', 2)"
                :options="availableServices"
                required
                @filter="onServiceFilter"
              />
              <service-selector
                v-else
                v-model="editItem.service"
                :label="i18n.t('common.service')"
                :options="availableServices"
                :rules="[val => !!val || i18n.t('validation.required')]"
                use-input
                @filter="onServiceFilter"
              />
              <stepper-navigation
                :back="prevStep"
                :continue="() => { markAttempted(2); form.validate().then(success => (success ? nextStep() : null)) }"
              />
            </q-step>
            <q-step
              :name="3"
              :title="i18n.t('common.rateCardInfo')"
              icon="mdi-information-outline"
              :done="!!editItem.id"
              :header-nav="!!editItem.id"
            >
              <rate-card-info
                :model-value="editItem"
                :mobile="isMobile"
                field-prefix=""
                @update:model-value="editItem = $event"
              />
              <stepper-navigation
                :back="prevStep"
                :finish="editItem.id ? null : onUpdate"
                :finish-label="i18n.t('common.addListingItem')"
              />

            </q-step>
          </q-stepper>
        </q-form>
      </q-card-section>
    </q-card-section>
    <q-card-actions v-if="editItem?.id" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="onConfirmDelete"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :disable="editItem.name === ''"
        :label="i18n.t('common.update')"
        @click="onUpdate"
      />
    </q-card-actions>

    <group-name-dialog
      v-model="groupDialogOpen"
      :default-name="editItem.name"
      :loading="isCreatingGroup"
      @confirm="onGroupConfirm"
    />
  </q-card>
</template>

<style scoped>
</style>
